import { Link, router, usePage } from "@inertiajs/react";
import { ErrorMessage, Field, Formik } from "formik";
import Button from "hardcover-ui/components/Button";
import Label from "hardcover-ui/components/Label";
import LoadingSpinner from "hardcover-ui/components/LoadingSpinner";
import Text from "hardcover-ui/components/Text";
import TextLink from "hardcover-ui/components/TextLink";
import { formInputTheme } from "hardcover-ui/theme/components/form/input";
import { Dispatch, SetStateAction, useState } from "react";
import { toast } from "sonner";
import { DefaultPageType } from "types/DefaultPageType";
import { boolean, object, ref, string } from "yup";
import Referrer from "./Referrer";

interface Props {
  onComplete: Dispatch<SetStateAction<boolean>>;
  complete: boolean;
}

const JoinForm = ({ complete, onComplete: setComplete }: Props) => {
  const { rootState } = usePage<DefaultPageType>().props;
  const referrerId = rootState.currentUser?.session?.referrerId || null;
  const referrerUrl = rootState.currentUser?.session?.referrerUrl || null;
  const [error, setError] = useState(null);

  const handleJoin = async (values, { setSubmitting }) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
      const response = await fetch("/users/join", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          user: {
            email: values.email,
            password: values.password,
            referrer_id: referrerId,
            referrer_url: referrerUrl
          }
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setComplete(true);
        setError(null);
        toast.success("Welcome to Hardcover! Let's get to know each other a little...", { duration: 2000 });
        router.reload();
        setTimeout(() => {
          router.visit("/welcome");
        }, 2000);
      } else {
        setError(data.error || data.message || "Join failed");
        toast.error(data.error || data.message || "Join failed");
      }
    } catch {
      console.error('Join error:', error);
      setError("An error occurred while joining");
      toast.error("An error occurred while joining");
    } finally {
      setSubmitting(false);
    }
  };

  if (complete) {
    return (
      <div className="h-64 flex justify-center flex-col">
        <LoadingSpinner size="lg" variant="layout" />
      </div>
    );
  }

  return (
    <div>
      <Referrer userId={referrerId} />
      <Formik
        initialValues={{ email: "", password: "", tos: false }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={object({
          email: string()
            .max(500, "Must be 500 characters or less")
            .email("Invalid email address")
            .required("Please enter your email"),
          password: string()
            .min(6, "Must be at least 6 characters")
            .max(128, "Must be 128 characters or less")
            .required("Please enter your password"),
          tos: boolean().oneOf([true], "Please agree to the Hardcover terms"),
          confirmPassword: string().oneOf(
            [ref("password")],
            "Passwords must match"
          ),
        })}
        onSubmit={handleJoin}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className="space-y-4 mt-4">
            {error && (
              <Text variant="warning" className="py-2">
                {error}
              </Text>
            )}
            <div>
              <Label size="lg">
                Email
                <Field
                  name="email"
                  aria-label="enter your email"
                  aria-required="true"
                  type="email"
                  autoFocus
                  className={formInputTheme.variant.normal}
                />
              </Label>
              <Text variant="warning">
                <ErrorMessage name="email" />
              </Text>
            </div>
            <div>
              <Label size="lg">
                Password
                <Field
                  name="password"
                  aria-label="enter your password"
                  aria-required="true"
                  type="password"
                  className={formInputTheme.variant.normal}
                />
              </Label>
              <Text variant="warning">
                <ErrorMessage name="password" />
              </Text>
            </div>
            <div>
              <Label size="lg">
                Confirm Password
                <Field
                  name="confirmPassword"
                  aria-label="confirm your password"
                  aria-required="true"
                  type="password"
                  className={formInputTheme.variant.normal}
                />
              </Label>
              <Text variant="warning">
                <ErrorMessage name="password" />
              </Text>
            </div>
            <div>
              <Label size="md" className="flex items-center">
                <Field
                  name="tos"
                  aria-label="accept the terms of service"
                  aria-required="true"
                  type="checkbox"
                  className="form-checkbox focus:ring-indigo-500 h-8 w-8 text-indigo-600 border-gray-300 rounded-sm cursor-pointer"
                />
                <span className="ml-3">
                  I agree to abide by the{" "}
                  <TextLink href="/blog/code-of-conduct">
                    code of conduct
                  </TextLink>{" "}
                  &amp; all{" "}
                  <TextLink href="/pages/policies">Hardcover policies</TextLink>
                  .
                </span>
              </Label>
              <Text variant="warning">
                <ErrorMessage name="tos" />
              </Text>
            </div>

            <Button
              variant="primary"
              size="lg"
              type="submit"
              className="block w-full justify-center"
              isLoading={formik.isSubmitting}
            >
              <span>Join Hardcover</span>
            </Button>
          </form>
        )}
      </Formik>
      <Link href="/welcome" className="hidden">
        Welcome
      </Link>
    </div>
  );
};

export default JoinForm;